import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Search, User, ShoppingBag, Scissors, X } from 'lucide-react';

const TYPE_ICONS = { client: User, order: ShoppingBag, fabric: Scissors };
const TYPE_COLORS = { client: '#2563EB', order: 'var(--brand-navy)', fabric: '#9333EA' };
const TYPE_LABELS = { client: 'Client', order: 'Order', fabric: 'Fabric' };

export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const timerRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await searchAPI.search(q);
      setResults(res.data?.results || []);
      setSelectedIdx(-1);
    } catch { setResults([]); }
    setLoading(false);
  }, []);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (query.length >= 2) {
      timerRef.current = setTimeout(() => doSearch(query), 250);
    } else {
      setResults([]);
    }
    return () => clearTimeout(timerRef.current);
  }, [query, doSearch]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const go = (url) => { navigate(url); setOpen(false); setQuery(''); setResults([]); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && selectedIdx >= 0 && results[selectedIdx]) { go(results[selectedIdx].url); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={wrapperRef} className="relative w-full max-w-xs" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
        <Input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search clients, orders, fabrics..."
          className="pl-8 pr-7 h-8 text-xs rounded-lg bg-white/80 border-transparent focus:border-[color:var(--brand-gold)] focus:bg-white"
          style={{ transition: 'background-color 150ms, border-color 150ms' }}
          data-testid="global-search-input"
        />
        {query && (
          <button onClick={() => { setQuery(''); setResults([]); }} className="absolute right-2 top-1/2 -translate-y-1/2">
            <X className="w-3 h-3" style={{ color: 'var(--ink-muted)' }} />
          </button>
        )}
      </div>
      {open && (query.length >= 2) && (
        <div className="absolute top-full mt-1 left-0 right-0 z-50 rounded-xl border bg-white shadow-[0_20px_60px_rgba(16,24,40,0.16)] overflow-hidden"
          style={{ borderColor: 'rgba(27,42,74,0.08)' }} data-testid="search-results-dropdown">
          {loading && <p className="text-xs px-4 py-3" style={{ color: 'var(--ink-muted)' }}>Searching...</p>}
          {!loading && results.length === 0 && <p className="text-xs px-4 py-3" style={{ color: 'var(--ink-muted)' }}>No results for "{query}"</p>}
          {!loading && results.map((r, i) => {
            const Icon = TYPE_ICONS[r.type] || Search;
            const color = TYPE_COLORS[r.type] || '#6B7280';
            return (
              <button key={`${r.type}-${r.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${i === selectedIdx ? 'bg-gray-50' : ''}`}
                onClick={() => go(r.url)}
                data-testid={`search-result-${r.type}-${r.id}`}>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + '10' }}>
                  <Icon className="w-3.5 h-3.5" style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: 'var(--brand-navy)' }}>{r.label}</p>
                  <p className="text-[9px] truncate" style={{ color: 'var(--ink-muted)' }}>{r.sub}</p>
                </div>
                <span className="text-[8px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: color + '08', color }}>{TYPE_LABELS[r.type]}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
